import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import Button from 'components/UI/V3/Button/Button';
import { StaffMember } from 'model/Service';
import Staff from 'model/Staff';
import StaffSelector from 'components/UI/StaffSelector/StaffSelector';
import StaffChip from 'components/UI/StaffChip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import styles from './EvaluationStaffSelector.module.scss';
import { showError } from '@spike/notifications-action';

interface EvaluationStaffSelectorProps {
    selectedStaff: StaffMember[];
    onUpdateStaff: (staff: StaffMember[]) => void;
}

export const EvaluationStaffSelector: React.FC<EvaluationStaffSelectorProps> = ({ selectedStaff, onUpdateStaff }) => {
    const [showStaffSelector, setShowStaffSelector] = useState(false);
    const dispatch = useDispatch();

    const allActiveStaff: StaffMember[] = useSelector<RootState, Staff[]>(state => state.staff.staff)
        .filter(staff => staff.active && !staff.deleted)
        .map(staff => ({
            id: staff.id!,
            firstName: staff.person.firstName,
            lastName: staff.person.lastName,
            avatar: staff.person.avatar
        }));

    // Auto-select all staff for new services
    useEffect(() => {
        if (allActiveStaff.length > 0 && selectedStaff.length === 0) {
            onUpdateStaff(allActiveStaff);
        }
    }, [allActiveStaff, selectedStaff.length, onUpdateStaff]);

    const handleAddStaff = (newStaff: StaffMember[]) => {
        const updatedStaff = [...selectedStaff, ...newStaff];
        onUpdateStaff(updatedStaff);
        setShowStaffSelector(false);
    };

    const handleRemoveStaff = (staffToRemove: StaffMember) => {
        if (selectedStaff.length > 1) {
            const updatedStaff = selectedStaff.filter(staff => staff.id !== staffToRemove.id);
            onUpdateStaff(updatedStaff);
        } else {
            dispatch(showError('The service must have at least one staff assigned.'));
        }
    };

    const openStaffSelector = () => {
        setShowStaffSelector(true);
    };

    // Available staff (not already selected)
    const availableStaff = allActiveStaff.filter(staff => !selectedStaff.some(selected => selected.id === staff.id));

    const addButton =
        selectedStaff.length > 0 ? (
            <FontAwesomeIcon
                icon={faPlusCircle}
                className={styles.plusIcon}
                onClick={openStaffSelector}
            />
        ) : (
            <Button
                label="Add Staff Member"
                variant="outlined"
                leftIcon={faPlus}
                onClick={openStaffSelector}
                className={styles.addButton}
            />
        );

    if (allActiveStaff.length === 0) {
        return (
            <div className={styles.section}>
                <Label>Add Staff Members</Label>
                <div className={styles.noStaffContainer}>
                    <p className={styles.noStaffTitle}>No Staff members have been created yet.</p>
                    <p className={styles.noStaffDescription}>
                        You can come back to add staff once you create them to activate the service.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.section}>
            <Label required>Add Staff Members</Label>

            <div className={styles.staffContainer}>
                {selectedStaff.map((staff, index) => (
                    <div
                        key={staff.id}
                        className={styles.staffChip}
                    >
                        <StaffChip
                            id={`evaluation_staff_${index}`}
                            staff={staff}
                            small={false}
                            onDelete={() => handleRemoveStaff(staff)}
                        />
                    </div>
                ))}

                {availableStaff.length > 0 && !showStaffSelector && addButton}
            </div>

            {showStaffSelector && (
                <div className={styles.staffSelectorContainer}>
                    <StaffSelector
                        id="evaluation_staff_selector"
                        staff={availableStaff}
                        showAddNew={false}
                        onBack={() => setShowStaffSelector(false)}
                        onSelect={handleAddStaff}
                    />
                </div>
            )}
        </div>
    );
};

export default EvaluationStaffSelector;
